import type { GtmEcommerce, GtmEcommerceItem } from '@/services/gtm'

/**
 * The list identity every report from a route page carries. It is what separates a
 * visitor who researched a route organically from one who ran a live search, so it is
 * shared with the sibling surfaces rather than named locally.
 */
const C2C_ITEM_LIST_ID = 'c2c_page'

/**
 * The list's human label, the same for every route. The platform pairs the id and the
 * name as ONE dimension, so a per-route name would split the list into a row per route
 * and cost the dimension the only thing it is for — counting route-page impressions.
 * A route's own detail belongs on its item's name.
 */
const C2C_ITEM_LIST_NAME = 'City to City page'

/**
 * The primary category, for every mode. The server container's margin template branches
 * on it, so a mode placed here would split one product across branches — the mode goes
 * in the secondary category and in the identity suffix instead.
 */
const TICKET_CATEGORY = 'ticket'

const TRAVEL_VERTICAL = 'travel'

/** A route page shows the exact route the visitor asked for; no other value is reachable here. */
const DIRECT_MATCH = 'direct'

interface RouteIdentity {
    departWcityid?: string
    arriveWcityid?: string
    mode: string
}

export interface C2CRouteListParams extends RouteIdentity {
    /** The CANONICAL city names, not the page's localized display names: these leave the
     *  page as data, and a localized one would split one route into a row per locale. */
    fromCity: string
    toCity: string
    /** The route's cheapest fare in the canonical currency, whatever currency the page
     *  rendered. Absent when the route quotes none. */
    cheapestPriceUsd?: number
    /** The schedule date the page previews (`YYYY-MM-DD`). */
    departDate?: string
}

export interface C2CTripSelectionParams extends RouteIdentity {
    tripId: string
    carrierCode?: string
    /** The trip's canonical-currency fare, not the one the card rendered. */
    priceUsd?: number
    /** The trip's place in the rendered list. */
    position: number
    /** The date the selected departure runs (`YYYY-MM-DD`). */
    departDate?: string
}

/** Fares reach the platforms as money, so a long conversion remainder is noise. */
function toFare(price: number | undefined): number | undefined {
    return price === undefined ? undefined : Math.round(price * 100) / 100
}

function cityCode(wcityid: string | undefined): string | undefined {
    const trimmed = wcityid?.trim()
    return trimmed ? trimmed.toUpperCase() : undefined
}

/**
 * The route's product id, or nothing when either endpoint is unknown. A partial id is not
 * a gap in a report but a collision: every such route would share one product id, and the
 * platform cannot tell those rows apart afterwards.
 */
function routeIdentity({ departWcityid, arriveWcityid, mode }: RouteIdentity) {
    const origin = cityCode(departWcityid)
    const destination = cityCode(arriveWcityid)
    if (!origin || !destination) {
        return undefined
    }
    // Normalised once and reused, so the mode reads the same wherever the item states it.
    const transMode = mode.toLowerCase()
    return { origin, transMode, itemId: `${origin}_${destination}_${transMode}` }
}

/** Only the keys whose value the caller could state — an absent key reports "not supplied". */
function withOptional(
    item: GtmEcommerceItem,
    optional: Partial<GtmEcommerceItem>,
): GtmEcommerceItem {
    for (const [key, value] of Object.entries(optional)) {
        if (value !== undefined) {
            Object.assign(item, { [key]: value })
        }
    }
    return item
}

/** The report a route page makes about the route it is showing. */
export function buildC2CRouteList(params: C2CRouteListParams): GtmEcommerce | undefined {
    const identity = routeIdentity(params)
    if (!identity) {
        return undefined
    }
    const { fromCity, toCity, cheapestPriceUsd, departDate } = params
    const routeName = `${fromCity} to ${toCity}`

    const item = withOptional(
        {
            item_id: identity.itemId,
            item_name: routeName,
            item_category: TICKET_CATEGORY,
            item_category2: identity.transMode,
            quantity: 1,
            index: 0,
            google_business_vertical: TRAVEL_VERTICAL,
            origin: identity.origin,
            match_type: DIRECT_MATCH,
        },
        // A route page previews one day, so the journey's two ends are the same date.
        { price: toFare(cheapestPriceUsd), start_date: departDate, end_date: departDate },
    )

    return {
        item_list_id: C2C_ITEM_LIST_ID,
        item_list_name: C2C_ITEM_LIST_NAME,
        match_type: DIRECT_MATCH,
        items: [item],
    }
}

/** The report a route page makes about the departure a visitor picked from it. */
export function buildC2CTripSelection(params: C2CTripSelectionParams): GtmEcommerce | undefined {
    const identity = routeIdentity(params)
    if (!identity) {
        return undefined
    }
    const { tripId, carrierCode, priceUsd, position, departDate } = params

    const item = withOptional(
        {
            item_id: identity.itemId,
            item_variant: tripId,
            item_category: TICKET_CATEGORY,
            item_category2: identity.transMode,
            quantity: 1,
            index: position,
            google_business_vertical: TRAVEL_VERTICAL,
            origin: identity.origin,
            match_type: DIRECT_MATCH,
        },
        {
            item_brand: carrierCode,
            price: toFare(priceUsd),
            // A one-way departure's two ends coincide; the destination expires an item by
            // its end date, so an item carrying only a start never expires.
            start_date: departDate,
            end_date: departDate,
        },
    )

    return {
        item_list_id: C2C_ITEM_LIST_ID,
        item_list_name: C2C_ITEM_LIST_NAME,
        match_type: DIRECT_MATCH,
        items: [item],
    }
}
